import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './auth.css';

/**
 * OAuth callback page — handles the redirect from Cognito hosted UI
 * after a user authenticates with Google (or Apple in future).
 *
 * Flow:
 * 1. Cognito redirects here with ?code=<authorization_code>
 * 2. We exchange the code for tokens via POST /v1/auth/social
 * 3. On success, redirect to the page the user came from (or home)
 */
export function OAuthCallbackPage() {
  const { loginSocial } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDescription || 'Authentication was cancelled or failed.');
      return;
    }

    if (!code) {
      setError('No authorization code received. Please try again.');
      return;
    }

    // Retrieve the provider and return path saved before redirect
    const provider = (localStorage.getItem('ws_oauth_provider') as 'google' | 'apple') || 'google';
    const returnTo = localStorage.getItem('ws_oauth_return_to') || '/';

    // Clean up stored OAuth state
    localStorage.removeItem('ws_oauth_provider');
    localStorage.removeItem('ws_oauth_return_to');

    // Check if this is a new user (no existing ws_user in localStorage)
    const isNewUser = !localStorage.getItem('ws_user');

    loginSocial(provider, code)
      .then(() => {
        if (isNewUser) {
          sessionStorage.setItem('ws_just_signed_up', 'true');
        }
        navigate(returnTo, { replace: true });
      })
      .catch((err) => {
        console.error('Social login failed:', err);
        setError('Unable to complete sign-in. Please try again.');
      });
  }, [searchParams, loginSocial, navigate]);

  if (error) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h1>Sign-in failed</h1>
          <div className="auth-error" role="alert">{error}</div>
          <button
            type="button"
            className="auth-submit"
            onClick={() => navigate('/auth/login', { replace: true })}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Signing you in...</h1>
        <p className="auth-subtitle">Please wait while we complete authentication.</p>
      </div>
    </div>
  );
}
